import { create } from 'zustand'
import { updateProfile } from '../api/auth'
import { useAuthStore } from './auth'

const STORAGE_KEY = 'optimus.workspaceId'

interface WorkspaceState {
    workspaceId: number | null
    setWorkspace: (id: number) => void
    reset: () => void
}

function readStoredId(): number | null {
    const raw = localStorage.getItem(STORAGE_KEY)
    const id = raw ? Number(raw) : NaN
    return Number.isFinite(id) ? id : null
}

export const useWorkspaceStore = create<WorkspaceState>()(set => ({
    workspaceId: readStoredId(),
    setWorkspace: id => {
        localStorage.setItem(STORAGE_KEY, String(id))
        set({ workspaceId: id })
        // lastWorkspaceId serverseitig persistieren (fire-and-forget)
        void updateProfile({ lastWorkspaceId: id }).catch(() => undefined)
    },
    reset: () => {
        localStorage.removeItem(STORAGE_KEY)
        set({ workspaceId: null })
    },
}))

/**
 * Aktiver Workspace: lokale Auswahl, sonst lastWorkspaceId, sonst erste
 * Membership. Antwortet null, solange kein Workspace existiert (Onboarding).
 */
export function useActiveWorkspaceId(): number | null {
    const user = useAuthStore(s => s.user)
    const selected = useWorkspaceStore(s => s.workspaceId)

    const ids = (user?.workspaces ?? []).map(w => w.workspace.id)
    if (selected !== null && ids.includes(selected)) return selected
    if (user?.lastWorkspaceId && ids.includes(user.lastWorkspaceId))
        return user.lastWorkspaceId
    return ids[0] ?? null
}

/** Rolle im aktiven Workspace: OWNER/ADMIN dürfen verwalten. */
export function useWorkspaceRole() {
    const user = useAuthStore(s => s.user)
    const workspaceId = useActiveWorkspaceId()
    if (!user || workspaceId === null) return null
    return user.workspaces.find(w => w.workspace.id === workspaceId)?.role ?? null
}

export function useCanManage(): boolean {
    const role = useWorkspaceRole()
    return role === 'OWNER' || role === 'ADMIN'
}
